/**
 * StreamflowWidget.ts
 *
 * Real-time USGS and provincial river streamflow telemetry card.
 * Displays nearest river basin station, real-time discharge (cfs), and flow percentile.
 */

import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class StreamflowWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'USGS River Streamflow';
    const opts = widget.options || {};

    const stationName = opts.stationName || opts.riverName || 'Regional River Gauge';
    const discharge = opts.dischargeCfs !== undefined ? opts.dischargeCfs : 1200;
    const percentile = opts.percentile !== undefined ? opts.percentile : 58;

    let statusText = 'Normal Seasonal Flow';
    let statusColor = '#10b981';
    let statusBg = 'rgba(16, 185, 129, 0.15)';

    if (percentile >= 90) {
      statusText = 'Flood Threat';
      statusColor = '#ef4444';
      statusBg = 'rgba(239, 68, 68, 0.2)';
    } else if (percentile >= 75) {
      statusText = 'High Flow';
      statusColor = '#f59e0b';
      statusBg = 'rgba(245, 158, 11, 0.15)';
    } else if (percentile < 25) {
      statusText = 'Low Flow';
      statusColor = '#fb923c';
      statusBg = 'rgba(251, 146, 60, 0.15)';
    }

    const latDisplay = ctx.lat ? `${Math.abs(ctx.lat).toFixed(2)}°${ctx.lat >= 0 ? 'N' : 'S'}` : '';

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🌊 ${title}</span>
          <span style="background: rgba(6, 182, 212, 0.15); color: #06b6d4; border: 1px solid rgba(6, 182, 212, 0.3); font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;">USGS Live Gauge</span>
        </div>
        <div style="background: rgba(15, 23, 42, 0.6); padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06); margin-bottom: 6px;">
          <div style="font-size: 0.68rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">${stationName}</div>
          <div style="display: flex; align-items: baseline; gap: 8px; margin: 4px 0;">
            <span style="font-size: 1.8rem; font-weight: 800; color: #38bdf8; font-family: monospace;">${discharge.toLocaleString()}</span>
            <span style="font-size: 0.85rem; color: #94a3b8; font-weight: 600;">cfs</span>
            <span style="margin-left: auto; font-size: 0.72rem; padding: 2px 8px; background: ${statusBg}; color: ${statusColor}; border: 1px solid ${statusColor}40; border-radius: 999px; font-weight: 700;">
              ${statusText} (${percentile}th %)
            </span>
          </div>
        </div>
        <div style="font-size: 0.68rem; color: #64748b; display: flex; justify-content: space-between;">
          <span>Active Basin Gauge ${latDisplay ? `· Near ${latDisplay}` : ''}</span>
          <span>Updated within 1h</span>
        </div>
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {}
}
